import { action, makeObservable, observable } from 'mobx';
import { AcvtPromotion, AcvtGame, AcvtHomeResult } from '../types/acvt';
import type { AppTarget } from '../types/app-selection';
import { RootStore } from './root';
import { HttpMethod } from '../types';

const params = {
    includeGamesV2: 'true',
};

/**
 * Builds the BU header attached to every `/api-mobile` request issued
 * from the preview. The mobile API reads `x-app-target` to scope the
 * response to a given BU (see `api-mobile/middlewares.ts`), so passing
 * it from here is enough to filter promotions / games / home data
 * without any backend change.
 */
const headersForRegion = (
    region: AppTarget | undefined
): Record<string, string> | undefined =>
    region ? { 'x-app-target': region as string } : undefined;

/**
 * The three preview slices (`promotions`, `games`, `home`) are fetched
 * independently and can be in flight at the same time. We can't reuse
 * the shared `WithNetworkConcurrency` helper here because it tracks a
 * single `latestTag` per instance — under rapid region/lang switches
 * (e.g. QC → CA → QC) a freshly-issued `fetchAcvtHome` would invalidate
 * the still-pending `fetchAcvtPromotions` and the previous response
 * would silently win the race. So we keep one tag per slice.
 */
type SliceKey = 'promotions' | 'games' | 'home';

export class AcvtStore {
    @observable acvtPromotionList: AcvtPromotion[] = [];
    @observable acvtGamesList: AcvtGame[] = [];
    @observable acvtHomeObject: AcvtHomeResult = {};

    private latestTags: Record<SliceKey, symbol> = {
        promotions: Symbol(),
        games: Symbol(),
        home: Symbol(),
    };

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        makeObservable(this);
        this.rootStore = rootStore;
    }

    private getTag(key: SliceKey) {
        const tag = Symbol();
        this.latestTags[key] = tag;
        return tag;
    }

    private isLatestTag(key: SliceKey, tag: symbol) {
        return this.latestTags[key] === tag;
    }

    @action
    async fetchAcvtPromotions(
        this: AcvtStore,
        lang: string,
        region?: AppTarget
    ) {
        const tag = this.getTag('promotions');
        const response = await this.rootStore.makeMobileNetworkCall<
            AcvtPromotion[]
        >(
            {
                method: HttpMethod.GET,
                url: '/promotions',
                headers: headersForRegion(region),
            },
            lang
        );

        if (!response.err && this.isLatestTag('promotions', tag)) {
            this.acvtPromotionList = response.data;
        }
    }

    @action
    async fetchAcvtGames(this: AcvtStore, lang: string, region?: AppTarget) {
        const tag = this.getTag('games');
        const response = await this.rootStore.makeMobileNetworkCall<AcvtGame[]>(
            {
                method: HttpMethod.GET,
                url: '/games',
                headers: headersForRegion(region),
            },
            lang
        );

        if (!response.err && this.isLatestTag('games', tag)) {
            this.acvtGamesList = response.data;
        }
    }

    @action
    async fetchAcvtHome(this: AcvtStore, lang: string, region?: AppTarget) {
        const tag = this.getTag('home');
        const response = await this.rootStore.makeMobileNetworkCall<
            AcvtHomeResult
        >(
            {
                method: HttpMethod.GET,
                url: '/home',
                params,
                headers: headersForRegion(region),
            },
            lang
        );

        if (!response.err && this.isLatestTag('home', tag)) {
            this.acvtHomeObject = response.data;
        }
    }
}
